import {
  FC,
  useState,
  useEffect,
  useRef,
  Fragment,
  useCallback,
  ChangeEvent,
} from 'react'
import {
  StyleWrapper,
  StyleRow,
  StyleColumn,
  StyleItem,
  StyleName,
  StyleMessage,
  StyleTime,
  StyleConversationListWrapper,
  StyleNameWrapper,
} from './style'
import ConversationHeader from '../header'
import Empty from '@commons/empty'
import Column from '@components/column'
import Skeleton from '@components/skeleton'
import Image from '@components/image'
import debounce from '@utils/debounce'
import { useSocket } from '@providers/SocketProvider'
import { useSelector } from 'react-redux'
import { Chat } from '@apis'
import { Actions, store } from '@store'
import useInfinityScroll from '@hooks/useInfinityScroll'
import { formatDistanceToNow } from 'date-fns'
interface ConversationListProps {
  sender: any
  receiver: any
}
const ConversationList: FC<ConversationListProps> = ({ sender }) => {
  const { socket }: any = useSocket()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [params, setParams] = useState({
    page: 1,
    limit: 15,
    search: null,
  })
  const {
    items: conversations,
    loading,
    totalPages,
  } = useSelector((state: any) => state.conversations)
  const { items: messages } = useSelector((state: any) => state.messages)
  const conversation = useSelector((state: any) => state.conversation)

  const handleClick = async (event) => {
    const target = event.target as HTMLElement
    const cross = target.closest('[data-cross-id]')
    if (!!cross) {
      const supportId = cross.getAttribute('data-cross-id')

      const find = conversations?.find(
        (conversation) => conversation.id == supportId,
      )

      store.dispatch(Actions.set('conversation', find))
    }
  }

  const fetchConversations = async (action: any, params: any) => {
    await Chat.getConversations({
      params,
      action,
    })
      .then((resp: any) => {
        store.dispatch(Actions.set('conversation', resp?.items?.[0] || {}))
      })
      .catch(() => {})
  }

  const loadMore = async () => {
    const updateParams = {
      ...params,
      page: params?.page + 1,
    }
    if (updateParams.page <= totalPages) {
      await fetchConversations('append', updateParams)
      setParams(updateParams)
    }
  }
  const { observeTarget }: any = useInfinityScroll({
    loading,
    hasMore: params?.page < totalPages ? true : false,
    onLoadMore: loadMore,
    rootRef: scrollContainerRef,
  })

  const handleSearch = debounce(async (value: string) => {
    const updateParams = { ...params, search: value }
    await fetchConversations('set', updateParams)
    setParams(updateParams)
  }, 600)

  const onReceiveMessage = useCallback(
    async (data) => {
      const findIndex = conversations.findIndex(
        (conversation: any) => conversation?.id == data?.conversationId,
      )
      if (findIndex >= 0) {
        let updatedConversation = {
          ...conversations[findIndex],
          messages: [data, ...(conversations[findIndex].messages || [])],
        }
        store.dispatch(Actions.update('conversations', updatedConversation))
        if (findIndex > 0) {
          store.dispatch(
            Actions.remove('conversations', { id: data?.conversationId }),
          )
          setTimeout(() => {
            store.dispatch(
              Actions.prepend('conversations', updatedConversation),
            )
          }, 600)
        }
      } else {
        store.dispatch(
          Actions.prepend('conversations', {
            id: data?.conversationId,
            messages: [data],
            user1: data?.user1,
            user2: data?.user2,
          }),
        )
      }
    },
    [messages?.length],
  )

  useEffect(() => {
    if (!conversations?.length) {
      fetchConversations('set', params)
    }
  }, [])

  useEffect(() => {
    if (!socket) return
    socket.on('receivedMessage', onReceiveMessage)
    return () => {
      socket.off('receivedMessage', onReceiveMessage)
    }
  }, [socket?.id, messages?.length])

  return (
    <>
      <ConversationHeader
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          handleSearch(event.target.value)
        }}
      />
      <StyleWrapper>
        <StyleConversationListWrapper
          ref={scrollContainerRef}
          onClick={handleClick}
        >
          {!!loading ? (
            <Column rowGap={5}>
              {Array(6)
                ?.fill(0)
                ?.map((_, index: number) => {
                  return <Skeleton key={index} width={`100%`} height={100} />
                })}
            </Column>
          ) : (
            <>
              {!!conversations?.length ? (
                <>
                  {conversations?.map((item: any, index) => {
                    const conversationUser =
                      item?.user1?.id == sender?.id ? item?.user2 : item?.user1
                    const isSentByMe = sender?.id == item?.messages[0]?.senderId
                    const shouldObserve = index === conversations.length - 1
                    return (
                      <Fragment key={item?.id}>
                        <StyleItem
                          isActive={conversation?.id == item?.id}
                          ref={shouldObserve ? observeTarget : null}
                          data-cross-id={item?.id}
                        >
                          <StyleRow>
                            <Image
                              src={`${process.env.BUCKET_URL}/${conversationUser?.avatar}`}
                              width={40}
                              height={40}
                              styleImage="border-radius:50%;"
                              fallbackType="user"
                            />
                            <StyleColumn>
                              <StyleNameWrapper>
                                <StyleName>{conversationUser?.name}</StyleName>
                                <StyleTime>
                                  {formatDistanceToNow(
                                    item?.messages?.[0]?.createdAt ||
                                      new Date(),
                                    {
                                      addSuffix: true,
                                    },
                                  )
                                    .replace(/^about /, '')
                                    .replace('less than', '')}
                                </StyleTime>
                              </StyleNameWrapper>
                              <StyleMessage>
                                {!!item?.messages[0]?.infos?.message ? (
                                  <>
                                    {!!isSentByMe
                                      ? `You: ${item?.messages[0]?.infos?.message}`
                                      : item?.messages[0]?.infos?.message
                                        ? `${item?.messages[0]?.infos?.message}`
                                        : 'No Message'}
                                  </>
                                ) : (
                                  <>
                                    {!!item?.messages[0]?.infos?.files?.length
                                      ? `${!!isSentByMe ? `You sent a file` : `${conversationUser?.name} sent a file`}`
                                      : 'No Message'}
                                  </>
                                )}
                              </StyleMessage>
                            </StyleColumn>
                          </StyleRow>
                        </StyleItem>
                      </Fragment>
                    )
                  })}
                </>
              ) : (
                <Empty />
              )}
            </>
          )}
        </StyleConversationListWrapper>
      </StyleWrapper>
    </>
  )
}

export default ConversationList
